import { DAY_NAMES_FULL, MONTH_NAMES } from '../types';
import type { EventInstance, TimeFormat } from '../types';
import type { WeatherDay } from '../types';
import { getAllDayEvents, isToday, formatDateKey } from '../utils';
import { AllDayRow } from './AllDayRow';
import { TimeGrid } from './TimeGrid';
import { WeatherIcon } from './WeatherIcon';

interface DayViewProps {
  currentDate: Date;
  events: EventInstance[];
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onClickSlot: (date: Date, hour: number) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  weather?: Map<string, WeatherDay>;
  hourHeightPx?: number;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
}

export function DayView({
  currentDate,
  events,
  workingHoursStart,
  timeFormat,
  onClickSlot,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  weather,
  hourHeightPx,
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
}: DayViewProps) {
  const today = isToday(currentDate);
  const allDayColumns = [{ date: currentDate, events: getAllDayEvents(events, currentDate) }];
  const dateKey = formatDateKey(currentDate);
  const dayWeather = weather?.get(dateKey);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Day header */}
      <div className="flex border-b">
        <div className="w-14 shrink-0" />
        <div className={`flex-1 text-center py-2 ${today ? 'bg-primary/5' : ''}`}>
          <div className="text-xs text-muted-foreground uppercase">
            {DAY_NAMES_FULL[currentDate.getDay()]}
          </div>
          <div
            className={`text-lg font-semibold ${
              today
                ? 'bg-primary text-primary-foreground rounded-full w-9 h-9 flex items-center justify-center mx-auto'
                : ''
            }`}
          >
            {currentDate.getDate()}
          </div>
          <div className="text-xs text-muted-foreground">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
          {dayWeather && (
            <div className="flex items-center justify-center gap-1 mt-0.5">
              <WeatherIcon code={dayWeather.weatherCode} size={16} />
              <span className="text-xs text-muted-foreground">
                {Math.round(dayWeather.tempMin)}°–{Math.round(dayWeather.tempMax)}°
              </span>
            </div>
          )}
        </div>
      </div>

      {/* All-day row */}
      <AllDayRow
        dayColumns={allDayColumns}
        onClickEvent={onClickEvent}
        eventColorPriority={eventColorPriority}
      />

      {/* Time grid (single column) */}
      <TimeGrid
        days={[currentDate]}
        events={events}
        workingHoursStart={workingHoursStart}
        timeFormat={timeFormat}
        onClickSlot={onClickSlot}
        onClickEvent={onClickEvent}
        resizingEventId={resizingEventId}
        resizeHeight={resizeHeight}
        onResizeStart={onResizeStart}
        hourHeightPx={hourHeightPx}
        dimPastEvents={dimPastEvents}
        eventColorPriority={eventColorPriority}
        showConflictWarnings={showConflictWarnings}
        minBreakBetweenEvents={minBreakBetweenEvents}
      />
    </div>
  );
}
